import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Package, X } from 'lucide-react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import Modal from '../../components/Modal';
import { normalizeAlaCarteItem, PRICING_STATUS } from '../../utils/alaCarteCatalog';
import { getTeamCatalogPageTitle, teamAlaCartePaths } from '../../utils/teamAlaCarteRoutes';
import { POPULAR_EMOJIS } from '../../data/alaCartePopularEmojis';
import PortalBackground from '../../components/PortalBackground';

/** Catalog cards: Draft | Unpromoted | In Review | Published */
function getCatalogCardStatus(pricingStatus) {
  if (pricingStatus === PRICING_STATUS.published) return { label: 'Published', variant: 'published' };
  if (pricingStatus === PRICING_STATUS.draft) return { label: 'Draft', variant: 'draft' };
  if (pricingStatus === PRICING_STATUS.unpromoted) return { label: 'Unpromoted', variant: 'draft' };
  return { label: 'In Review', variant: 'inReview' };
}

function catalogStatusPillClass(variant) {
  switch (variant) {
    case 'published':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case 'draft':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
  }
}

/**
 * @param {{ department: 'Marketing'|'Finance'|'HR' }} props
 */
export default function TeamAlaCarteCatalog({ department }) {
  const navigate = useNavigate();
  const { catalog: catalogPath } = teamAlaCartePaths(department);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    icon: '📦',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'alaCarteItems'));
      const all = snap.docs.map((d) => normalizeAlaCarteItem(d.id, d.data()));
      setItems(all.filter((i) => i.department === department));
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [department]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const statusLabel = getCatalogCardStatus(item.pricingStatus).label;
      const hay = `${item.name || ''} ${item.description || ''} ${item.pricingStatus || ''} ${statusLabel}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, searchQuery]);

  const filteredEmojis = useMemo(
    () => POPULAR_EMOJIS.filter((e) => e && e.includes(emojiSearch)),
    [emojiSearch]
  );

  const handleAddItem = async () => {
    if (!newItem.name?.trim()) {
      alert('Please enter an item name.');
      return;
    }
    if (!String(newItem.description || '').trim()) {
      alert('Please enter a description.');
      return;
    }
    setSaving(true);
    try {
      const itemData = {
        name: newItem.name.trim(),
        description: String(newItem.description).trim(),
        icon: newItem.icon || '📦',
        department,
        pricingStatus: PRICING_STATUS.draft,
        pricingType: 'caseByCase',
        standardPricing: { cost: 0, unit: 'item', stripeProductId: '' },
        scope: '',
        deliverableRows: [],
        teamInput: { scope: '', deliverableRows: [], internalCostEstimate: '' },
        internalOnly: { notes: '' },
        hasDeliverable: false,
        popularAddOn: false,
        exclusiveChoiceItemIds: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const docRef = await addDoc(collection(db, 'alaCarteItems'), itemData);
      setShowAddModal(false);
      setNewItem({ name: '', description: '', icon: '📦' });
      setEmojiSearch('');
      await load();
      navigate(`${catalogPath}/${docRef.id}`);
    } catch (err) {
      console.error(err);
      alert('Could not add item.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading catalog…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 pb-12 pt-4 relative">
      <PortalBackground />
      <div className="relative mx-auto max-w-7xl">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            {getTeamCatalogPageTitle(department)}
          </h1>
        </header>

        <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search items…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition hover:bg-red-700"
            >
              <Plus className="h-5 w-5" />
              Add New Item
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {filteredItems.map((item) => {
            const cardStatus = getCatalogCardStatus(item.pricingStatus);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(`${catalogPath}/${item.id}`)}
                className="rounded-2xl border border-gray-200 bg-white p-6 text-left shadow-sm transition hover:border-red-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-red-800"
              >
                <div className="mb-4 text-center text-4xl">{item.icon || '📦'}</div>
                <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-white">{item.name}</h3>
                <p className="mt-2 line-clamp-2 text-center text-sm text-gray-500 dark:text-gray-400">
                  {item.description || '—'}
                </p>
                <div className="mt-4 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${catalogStatusPillClass(
                      cardStatus.variant
                    )}`}
                  >
                    {cardStatus.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No items found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try another search or add a new item.</p>
          </div>
        )}
      </div>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} zIndex={50} maxWidth="md">
        <div className="p-6">
          <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">Add New Item</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Icon</label>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(true)}
                className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-2xl transition hover:border-red-500 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-red-400"
              >
                {newItem.icon}
              </button>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Item Name</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem((p) => ({ ...p, name: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="Enter item name"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem((p) => ({ ...p, description: e.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder="Describe this offering"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleAddItem}
              className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Create item'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showEmojiPicker} onClose={() => setShowEmojiPicker(false)} zIndex={60} maxWidth="lg">
        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Choose icon</h3>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowEmojiPicker(false)}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <input
            type="text"
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            placeholder="Filter…"
            className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <div className="max-h-64 overflow-y-auto">
            <div className="grid grid-cols-8 gap-2 sm:grid-cols-10">
              {filteredEmojis.slice(0, 120).map((em) => (
                <button
                  key={em}
                  type="button"
                  className="text-2xl hover:scale-110"
                  onClick={() => {
                    setNewItem((p) => ({ ...p, icon: em }));
                    setShowEmojiPicker(false);
                  }}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
